/**
 * ============================================================================
 * PUBLIC PROFILE VIEW PAGE - 100% Ready
 * ============================================================================
 * 
 * Page for viewing other users' public profiles
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';
import { useToast } from '../providers';
import { useI18n } from '../providers';
import { UserDashboardLayout } from '../components/user';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import Spinner from '../components/common/Spinner';
import StatusBadge from '../components/common/StatusBadge';
import UserProfile from '../components/user/UserProfile';
import UserStats from '../components/user/UserStats';
import UserActivity from '../components/user/UserActivity';
import Tabs from '../components/common/Tabs';
import EmptyState from '../components/common/EmptyState';
import '../styles/animations.css';
import '../styles/global.css';

/**
 * Public Profile View Page Component
 * @returns {JSX.Element}
 */
const PublicProfileView = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { getUserProfile, loading } = useUser();
  const { showError } = useToast();
  const { language } = useI18n();

  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);
        const data = await getUserProfile(userId);
        setUser(data);
      } catch (error) {
        showError(
          error.message || (language === 'fr' ? 'Échec du chargement du profil' : 'Failed to load profile')
        );
        console.error('Load profile error:', error);
        navigate('/dashboard');
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      loadProfile();
    }
  }, [userId, getUserProfile, showError, navigate, language]);

  if (loading) {
    return (
      <UserDashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spinner size="lg" />
        </div>
      </UserDashboardLayout>
    );
  }

  if (!user) {
    return (
      <UserDashboardLayout>
        <Card variant="glass" className="p-12">
          <EmptyState
            icon="👤"
            title={language === 'fr' ? 'Profil introuvable' : 'Profile Not Found'}
            description={
              language === 'fr'
                ? 'Le profil utilisateur demandé n\'existe pas ou n\'est pas accessible'
                : 'The requested user profile does not exist or is not accessible'
            }
            action={
              <Button
                variant="primary"
                onClick={() => navigate('/dashboard')}
                className="bg-gradient-to-r from-blue-600 to-green-600"
              >
                {language === 'fr' ? 'Retour au tableau de bord' : 'Back to Dashboard'}
              </Button>
            }
          />
        </Card>
      </UserDashboardLayout>
    );
  }

  const tabs = [
    { id: 'profile', label: language === 'fr' ? 'Profil' : 'Profile', icon: '👤' },
    { id: 'stats', label: language === 'fr' ? 'Statistiques' : 'Stats', icon: '📊' },
    { id: 'activity', label: language === 'fr' ? 'Activité' : 'Activity', icon: '📈' },
  ];

  return (
    <UserDashboardLayout>
      {/* Header */}
      <div className="mb-8 animate-fadeInUp">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4"
        >
          ← {language === 'fr' ? 'Retour' : 'Back'}
        </Button>
        <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
          👤 {language === 'fr' ? 'Profil utilisateur' : 'User Profile'}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          {language === 'fr'
            ? 'Voir le profil public de cet utilisateur'
            : 'View this user\'s public profile'}
        </p>
      </div>

      {/* Trust Metrics - Prominently Displayed */}
      {user && (user.stats || user.rating) && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
          <Card
            variant="glass"
            className="backdrop-blur-xl border-white/20 dark:border-white/10 bg-gradient-to-br from-blue-50/80 to-cyan-50/80 dark:from-blue-900/20 dark:to-cyan-900/20"
            hover
          >
            <div className="text-center">
              <div className="text-3xl font-black bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-1">
                {user.stats?.totalTransactions || user.totalTransactions || 0}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {language === 'fr' ? 'Transactions' : 'Transactions'}
              </div>
            </div>
          </Card>
          <Card
            variant="glass"
            className="backdrop-blur-xl border-white/20 dark:border-white/10 bg-gradient-to-br from-green-50/80 to-emerald-50/80 dark:from-green-900/20 dark:to-emerald-900/20"
            hover
          >
            <div className="text-center">
              <div className="text-3xl font-black bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-1">
                {user.stats?.successRate || user.successRate || 0}%
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {language === 'fr' ? 'Taux de Réussite' : 'Success Rate'}
              </div>
            </div>
          </Card>
          <Card
            variant="glass"
            className="backdrop-blur-xl border-white/20 dark:border-white/10 bg-gradient-to-br from-yellow-50/80 to-orange-50/80 dark:from-yellow-900/20 dark:to-orange-900/20"
            hover
          >
            <div className="text-center">
              <div className="text-3xl font-black bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent mb-1">
                ⭐ {user.rating?.average?.toFixed(1) || user.rating_average?.toFixed(1) || '0.0'}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {language === 'fr' ? 'Note Moyenne' : 'Average Rating'}
              </div>
            </div>
          </Card>
          <Card
            variant="glass"
            className="backdrop-blur-xl border-white/20 dark:border-white/10 bg-gradient-to-br from-purple-50/80 to-pink-50/80 dark:from-purple-900/20 dark:to-pink-900/20"
            hover
          >
            <div className="text-center">
              <div className="text-3xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-1">
                {user.rating?.count || user.rating_count || 0}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {language === 'fr' ? 'Avis' : 'Reviews'}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Verification Badge */}
      {user && (
        <Card
          variant="glass"
          className="mb-6 backdrop-blur-xl border-white/20 dark:border-white/10 bg-gradient-to-br from-green-50/80 to-emerald-50/80 dark:from-green-900/20 dark:to-emerald-900/20 animate-fadeInUp"
          style={{ animationDelay: '0.15s' }}
          hover
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center text-3xl shadow-lg shadow-green-500/30 animate-float">
                {user.verificationLevel === 2 ? '✅' : user.verificationLevel === 1 ? '⭐' : '🔒'}
              </div>
              <div>
                <h3 className="text-lg font-black text-gray-900 dark:text-white mb-1">
                  {language === 'fr' ? 'Niveau de Vérification' : 'Verification Level'}
                </h3>
                <StatusBadge
                  variant={
                    user.verificationLevel === 2
                      ? 'success'
                      : user.verificationLevel === 1
                      ? 'warning'
                      : 'default'
                  }
                  size="lg"
                >
                  {user.verificationLevel === 2
                    ? language === 'fr' ? 'Niveau 2 - Vérifié' : 'Level 2 - Verified'
                    : user.verificationLevel === 1
                    ? language === 'fr' ? 'Niveau 1 - En Attente' : 'Level 1 - Pending'
                    : language === 'fr' ? 'Niveau 0 - Non Vérifié' : 'Level 0 - Unverified'}
                </StatusBadge>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          variant="pills"
        />
      </Card>

      {/* Tab Content */}
      {activeTab === 'profile' && (
        <div className="space-y-6 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
          <UserProfile user={user} showActions={false} />
        </div>
      )}

      {activeTab === 'stats' && (
        <div className="space-y-6 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
          <UserStats userId={userId} />
        </div>
      )}

      {activeTab === 'activity' && (
        <div className="space-y-6 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
          <UserActivity userId={userId} period="month" />
        </div>
      )}
    </UserDashboardLayout>
  );
};

export default PublicProfileView;
